import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Lock, ArrowLeft } from "lucide-react";

export default function PreviewLesson() {
  const { slug, lessonId } = useParams();
  const nav = useNavigate();
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);

  useEffect(() => {
    api.get(`/api/courses/${slug}`).then(({ data }) => {
      setCourse(data);
      const allLessons = (data.modules || []).flatMap((m) => m.lessons);
      const l = allLessons.find((x) => x.id === lessonId);
      setLesson(l || null);
    });
  }, [slug, lessonId]);

  if (!course || !lesson) return <div className="py-24 text-center">Loading...</div>;
  if (!lesson.is_preview) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Lock className="w-10 h-10 mx-auto text-[#D92D20]" />
        <h2 className="font-display font-bold text-2xl mt-3">This lesson is locked</h2>
        <p className="text-[#4B5563] mt-2">Purchase the course to unlock all lessons, quizzes and the certificate.</p>
        <Link to={`/courses/${slug}`} className="btn-primary inline-block mt-6">Back to course</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="preview-lesson">
      <Link to={`/courses/${slug}`} className="text-sm text-[#D92D20] font-semibold inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to course
      </Link>
      <div className="label-eyebrow">{course.title} · Free Preview</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-2 tracking-tight">{lesson.title}</h1>

      <article className="prose max-w-none mt-6 text-[#111827] whitespace-pre-line leading-relaxed">
        {lesson.content}
      </article>

      {lesson.key_terms?.length > 0 && (
        <div className="mt-8 bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl p-5">
          <div className="label-eyebrow">Key terms</div>
          <div className="flex flex-wrap gap-2 mt-2">
            {lesson.key_terms.map((k) => (
              <span key={k} className="text-xs px-2.5 py-1 rounded-full bg-white border border-[#E5E7EB]">{k}</span>
            ))}
          </div>
        </div>
      )}

      {lesson.examples?.length > 0 && (
        <div className="mt-6">
          <div className="label-eyebrow">Examples</div>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-[#4B5563]">
            {lesson.examples.map((e, i) => <li key={`${e.slice(0, 32)}-${i}`}>{e}</li>)}
          </ul>
        </div>
      )}

      {lesson.summary && (
        <div className="mt-6 bg-[#064E3B] text-white rounded-xl p-5">
          <div className="label-eyebrow text-[#FECACA]">Summary</div>
          <p className="mt-2">{lesson.summary}</p>
        </div>
      )}

      <div className="mt-10 border-t border-[#E5E7EB] pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="font-display font-bold text-xl">Like what you see?</div>
          <div className="text-sm text-[#4B5563]">Get full access, quizzes, AI Tutor and your certificate.</div>
        </div>
        <button onClick={() => nav(`/courses/${slug}`)} data-testid="preview-enroll" className="btn-primary">Enroll Now — ${course.price_usd}</button>
      </div>
    </div>
  );
}
